import React from 'react';
import { ROUTES } from '@core/constants/routes/routes';
import { ClientSVG, ManagmentSVG, UsersSVG, PaymentLinksSVG, MoneySignSVG, ReportIconSVG } from '@ui/components';

/**
 * Single source of truth for the modules the shell knows about.
 *
 * Each entry declares two independent things:
 *
 * - **Routing**: `basePath` is the URL prefix the module owns and `load`
 *   resolves every route under it. No other module may declare routes under
 *   that prefix, and the module may not declare routes outside it.
 * - **Menu**: `menu` describes how the module shows up in the sidebar. A
 *   module with `menu: null` simply has no menu entry.
 *
 * `load` is a `React.lazy` component, so a module's code is downloaded the
 * first time somebody navigates into it instead of shipping in the initial
 * bundle. Two consequences worth knowing:
 *
 * - There are deliberately **no static imports of the modules in this file**.
 *   A single static import would pull that module back into the main bundle
 *   and silently undo the code splitting.
 * - Whoever renders these needs a Suspense boundary above them, and an error
 *   boundary for when the download fails. The Sidebar provides both.
 *
 * Array order matters for ROUTING only: the Switch renders the first match.
 * The order of the menu is a separate `menu.order`, so one can be changed
 * without touching the other.
 *
 * When a module moves to its own repository, only its `load` changes -
 * `import('./modules/cards.routes')` becomes `import('cards/Routes')`. The
 * prefixes, the ordering and the menu stay untouched.
 *
 * `/dashboards` and `/` are deliberately absent: they belong to the shell,
 * not to a module. The shell's own menu entries live in
 * `layout/sidebar/menu.js`.
 *
 * ---
 *
 * Shape of `menu`:
 *
 * | field         | meaning                                                |
 * |---------------|--------------------------------------------------------|
 * | `group`       | id of the group it hangs from, or `null` for top level  |
 * | `order`       | position within its level                               |
 * | `labelKey`    | i18n key of the label                                   |
 * | `path`        | where clicking it navigates                             |
 * | `matchPath`   | URL prefix that marks the item as active                |
 * | `icon`        | `(stroke) => JSX`, so every SVG keeps its own props     |
 * | `permission`  | permission needed to see it, or `null`                  |
 * | `featureFlag` | flag in `enabledPages` that hides it, or `null`         |
 * | `activeClass` | optional override of the active button class            |
 *
 * IMPORTANT: `featureFlag` lives inside `menu` on purpose - it must NEVER
 * filter the routing Switch. Today an item hidden by a flag still has its
 * routes registered, and entering by URL is stopped by the module's own
 * `ProtectedRoute`. Filtering the Switch instead would drop the user on the
 * "page not found" catch-all, which is a different behaviour.
 */
/**
 * Ids of the menu groups.
 *
 * A module's `menu.group` must match one of these, and `layout/sidebar/menu.js`
 * declares what each group looks like. The id is the contract between the two
 * files: a typo would silently drop the item out of its submenu and render it
 * at the top level instead, so it lives here in one place.
 */
export const MENU_GROUPS = Object.freeze({
    PRODUCTS: 'products',
});

export const modules = [
    {
        id: 'accounts',
        basePath: ROUTES.PRODUCTS.ACCOUNTS.BASE,
        load: React.lazy(() => import('./modules/accounts.routes')),
        menu: {
            group: MENU_GROUPS.PRODUCTS,
            order: 10,
            labelKey: 'menu.accounts',
            // The listing hangs one level below the module prefix, so where the
            // item navigates and what marks it active are not the same URL.
            path: ROUTES.PRODUCTS.ACCOUNTS.LIST,
            matchPath: ROUTES.PRODUCTS.ACCOUNTS.BASE,
            icon: (stroke) => <MoneySignSVG style={{ stroke, height: '1.846rem', width: '1.846rem', strokeWidth: '1.3' }} />,
            permission: null,
            // Deliberate: Accounts is gated by the Users flag. It reads odd but
            // it is a business decision, not a leftover - do not 'fix' it to
            // `enableAccounts` without asking. Turning `enableUsers` off is
            // meant to hide both sections at once.
            featureFlag: 'enableUsers',
        },
    },
    {
        id: 'clients',
        basePath: ROUTES.CLIENTS.BASE,
        load: React.lazy(() => import('./modules/clients.routes')),
        menu: {
            group: null,
            order: 20,
            labelKey: 'menu.clients',
            path: ROUTES.CLIENTS.BASE,
            matchPath: ROUTES.CLIENTS.BASE,
            icon: (stroke) => <ClientSVG customStroke={stroke} strokeWidth='1.3' />,
            permission: 'show_clients.main',
            // There is no `enableClients` flag, so this item is always visible.
            featureFlag: null,
        },
    },
    {
        id: 'cards',
        basePath: ROUTES.PRODUCTS.CARDS.BASE,
        load: React.lazy(() => import('./modules/cards.routes')),
        // Cards has routes but no menu entry: the item is hidden at the
        // business request (BM-365). Its routes keep working by URL.
        // To bring it back, replace `null` with:
        //   {
        //       group: MENU_GROUPS.PRODUCTS, order: 20, labelKey: 'menu.cards',
        //       path: ROUTES.PRODUCTS.CARDS.BASE,
        //       matchPath: ROUTES.PRODUCTS.CARDS.BASE,
        //       icon: (stroke) => <CardsSVG customStroke={stroke} />,
        //       permission: null, featureFlag: null,
        //   }
        menu: null,
    },
    {
        id: 'users',
        basePath: ROUTES.USERS.BASE,
        load: React.lazy(() => import('./modules/users.routes')),
        menu: {
            group: null,
            order: 50,
            labelKey: 'menu.users',
            path: ROUTES.USERS.BASE,
            matchPath: ROUTES.USERS.BASE,
            icon: (stroke) => <UsersSVG customStroke={stroke} strokeWidth='1.3' />,
            permission: 'show_users',
            featureFlag: 'enableUsers',
        },
    },
    {
        id: 'payments',
        basePath: ROUTES.PRODUCTS.PAYMENTS.BASE,
        load: React.lazy(() => import('./modules/payments.routes')),
        menu: {
            group: MENU_GROUPS.PRODUCTS,
            order: 30,
            labelKey: 'menu.paymentLinks',
            path: ROUTES.PRODUCTS.PAYMENTS.LINKS,
            // Only the links screen lights the item up - movements and
            // chargebacks are reached from the module's own submenu.
            matchPath: ROUTES.PRODUCTS.PAYMENTS.LINKS,
            icon: (stroke) => <PaymentLinksSVG customStroke={stroke} strokeWidth='1.3' />,
            permission: null,
            featureFlag: 'enablePaymentLinks',
        },
    },
    {
        id: 'management',
        basePath: ROUTES.MANAGEMENT.BASE,
        load: React.lazy(() => import('./modules/management.routes')),
        menu: {
            group: null,
            order: 60,
            labelKey: 'menu.management',
            path: ROUTES.MANAGEMENT.BASE,
            matchPath: ROUTES.MANAGEMENT.BASE,
            icon: (stroke) => <ManagmentSVG customStroke={stroke} strokeWidth='1.3' />,
            permission: 'show_management.main',
            // Deliberate: there is no `enableManagement` flag, so Management
            // rides on the Products one. Business decision - leave as is.
            featureFlag: 'enableProduct',
        },
    },
    {
        id: 'reports',
        // ROUTES.REPORTS.BASE is `/reports/exempts`, a page - not the prefix.
        basePath: '/reports',
        load: React.lazy(() => import('./modules/reports.routes')),
        menu: {
            group: null,
            order: 40,
            labelKey: 'menu.reports',
            path: ROUTES.REPORTS.BASE,
            matchPath: '/reports',
            icon: (stroke) => <ReportIconSVG customStroke={stroke} strokeWidth='1.3' />,
            permission: 'show_reports.main',
            // Deliberate: Reports is gated by permission only. `enableReports`
            // exists but is intentionally not wired to this item.
            featureFlag: null,
        },
    },
];

export default modules;
